import {
  paymentScreenIsClosed,
  paymentScreenIsLoaded,
} from '@wwe-portal/web/libs/analytics/offer';
import { XsollaService } from '@wwe-portal/web/libs/xsolla/create-xsolla-service';
import {
  InterstitialOffer,
  UpsellOffer,
} from '@wwe-portal/web/services/client/interstitial';
import { checkOfferValiditiy } from '@wwe-portal/web/services/client/offer';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { getXsollaToken } from '@wwe-portal/web/services/client/payment';
import { createStore } from 'zustand';
import {
  persist,
  createJSONStorage,
  subscribeWithSelector,
} from 'zustand/middleware';

export interface PaymentContext<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  screenId?: number;
  screenCategory?: string;
  currencies: {
    name: string;
    rate: number;
  }[];
  balance: {
    webCoin?: number;
    ruby?: number;
    clubCoin?: number;
  };
  currentUser:
    | {
        userId: string;
      }
    | undefined;
  upsellData?: {
    intersititalData: UpsellOffer | InterstitialOffer;
    offerId: string;
  };
  metadata?: T;
}

export interface Payment {
  source: string;
  offerData: OfferData;
  paymentId?: string;
  context: PaymentContext;
}

interface Tracker {
  onPaymentLoaded: (offerData: OfferData, context: PaymentContext) => void;
  onPaymentClosed: (offerData: OfferData, context: PaymentContext) => void;
}

interface PaymentStoreState {
  xsollaService?: XsollaService;
  pendingPayment: Payment | undefined;
  successPayment: Payment | undefined;

  tracker: Tracker | undefined;
}

interface PaymentStoreActions {
  startPayment: (
    payment: Payment,
    isSkipValidation: boolean | undefined
  ) => Promise<void>;
  clearPayments: () => void;
  setXsollaService: (xsollaService: XsollaService) => void;

  setPendingPayment: (payment: Payment) => void;
  setSuccessPayment: (paymentId: string | undefined) => void;
  clearSuccessPayment: () => void;

  addTracker: (tracker: Tracker) => void;
}

interface PaymentStore extends PaymentStoreState, PaymentStoreActions {}

const getXsollaVersion = () => {
  const body = document.getElementsByTagName('body')[0];
  return body?.clientWidth && body.clientWidth > 768 ? 'desktop' : 'mobile';
};

const createPaymentStore = () =>
  createStore(
    subscribeWithSelector(
      persist<PaymentStore, [], [], Pick<PaymentStore, 'pendingPayment'>>(
        (set, get) => {
          const setSuccessPayment = (paymentId: string | undefined) => {
            const pendingPayment = get().pendingPayment;

            if (!pendingPayment) {
              return;
            }

            const successPayment = {
              ...pendingPayment,
              paymentId,
            };

            set({
              // Clear pending payment when a payment is successful
              pendingPayment: undefined,
              successPayment,
            });
          };

          const setPendingPayment = (payment: Payment) =>
            set({ pendingPayment: payment });

          return {
            // State
            successPayment: undefined,
            xsollaService: undefined,
            pendingPayment: undefined,
            tracker: undefined,

            // Xsolla service
            setXsollaService: (xsollaService) => set({ xsollaService }),

            // Payment
            setSuccessPayment,
            setPendingPayment,
            startPayment: async (
              { offerData, source, context },
              isSkipValidation
            ) => {
              const xsollaService = get().xsollaService;
              if (!xsollaService) {
                throw new Error('Xsolla service not initialized');
              }
              const version = getXsollaVersion();
              setPendingPayment({
                source,
                offerData,
                paymentId: undefined,
                context,
              });

              // Validate offer
              if (!isSkipValidation) {
                const validationResult = await checkOfferValiditiy(
                  offerData.OffersID
                );
                if (!validationResult.data.data.success) {
                  throw new Error(validationResult.data.data.message);
                }
              }

              const token = await getXsollaToken({
                sku: offerData.XsollaID,
                offerId: offerData.OffersID,
                packageId: offerData.HardCurrencyPackageID,
                redirectUri: window.location.href,
                version: getXsollaVersion(),
              });

              setPendingPayment({
                source,
                offerData,
                paymentId: undefined,
                context,
              });

              const paymentInfo = await xsollaService.startPayment({
                token: token.data.data.token,
                version,
                onWidgetLoaded: () => {
                  const tracker = get().tracker;
                  if (tracker) {
                    tracker.onPaymentLoaded(offerData, context);
                  }
                },
                onWidgetClosed: () => {
                  const tracker = get().tracker;
                  if (tracker) {
                    tracker.onPaymentClosed(offerData, context);
                  }
                },
              });

              setSuccessPayment(paymentInfo?.invoice_id);
            },
            clearSuccessPayment: () => set({ successPayment: undefined }),
            clearPayments: () => set({ pendingPayment: undefined }),

            // Add events tracker
            addTracker: (tracker) => set({ tracker }),
          };
        },
        {
          name: 'payment-store',
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            pendingPayment: state.pendingPayment,
          }),
        }
      )
    )
  );

let paymentStore: ReturnType<typeof createPaymentStore>;

export const initializePaymentStore = () => {
  if (!paymentStore) {
    paymentStore = createPaymentStore();
    paymentStore.getState().addTracker({
      onPaymentLoaded: (offerData, context) => {
        paymentScreenIsLoaded(
          offerData.OffersID ?? '',
          context.screenCategory ?? ''
        );
      },
      onPaymentClosed: (offerData, context) => {
        paymentScreenIsClosed({
          offerData,
          analyticsData: {
            screen_category: context.screenCategory ?? '',
          },
        });
      },
    });
  }
};

export const getPaymentStore = () => {
  if (!paymentStore) {
    paymentStore = createPaymentStore();
  }
  return paymentStore;
};
